/* Bare trend shape for stat tiles — no axes, no tooltip; the tile carries the number. */

import { useId } from 'react';

interface Props {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
  /** Fills under the line at low opacity. */
  fill?: boolean;
}

export function Sparkline({ values, width = 84, height = 30, color = 'var(--accent)', fill = true }: Props) {
  // Derived from the render tree, not the data — two tiles with identical
  // numbers but different colors must not share a gradient.
  const id = `spark${useId().replace(/:/g, '')}`;
  if (values.length < 2) return null;

  const rawLo = Math.min(...values);
  const rawHi = Math.max(...values);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;

  /*
   * Stretching a near-constant series to full height turns ±0.05% of noise
   * into a dramatic zigzag — crash-free rate is the case that exposed this.
   * When the swing is under 2% of the mean, widen the domain so the line
   * stays visibly flat and reads as "no meaningful change".
   */
  const swing = rawHi - rawLo;
  const flatFloor = Math.abs(mean) * 0.02;
  const padDomain = swing < flatFloor ? (flatFloor - swing) / 2 : 0;
  const lo = rawLo - padDomain;
  const hi = rawHi + padDomain;

  const span = hi - lo || 1;
  const pad = 2;
  const stepX = (width - pad * 2) / (values.length - 1);

  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (1 - (v - lo) / span) * (height - pad * 2);
    return [x, y] as const;
  });

  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${height} L${pts[0][0].toFixed(1)},${height} Z`;

  /*
   * Drawn in a viewBox rather than at a fixed pixel width so the tile can
   * squeeze it when the number beside it is wide — "99.87%" and "12m 04s" at
   * 27px plus a rigid 84px SVG used to overflow the card in a four-across row.
   * `width` is now the *preferred* width, applied as a CSS basis; the stroke
   * is non-scaling so a squeezed sparkline keeps its 2px line.
   */
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      height={height}
      className="tile__spark"
      style={{ flexBasis: width }}
      aria-hidden="true"
      focusable="false"
    >
      {fill ? (
        <>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.26} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${id})`} />
        </>
      ) : null}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
